/**
 * Elevation tiers for the aether Surface system (Phase 2a).
 *
 * Each tier maps to a z-index value. The gaps of 100 between tiers leave room
 * for internal stacking within a tier (e.g. stacked modals each increment by 1).
 */
/**
 * Maps each elevation tier to its base z-index value.
 *
 * The scale is intentionally chosen to avoid collision with existing hardcoded
 * z-indices during incremental migration:
 *
 * - `base` (0): default stacking, no explicit z-index needed
 * - `raised` (100): elevated cards, floating panels, split panes
 * - `navigation` (200): sticky headers, sidebars, navbars
 * - `overlay` (300): menus, popovers, tooltips
 * - `modal` (400): modal dialogs and bottom sheets
 * - `toast` (500): notification toasts
 * - `critical` (600): system-level overlays, blockers
 */
export const ELEVATION_Z_INDEX = {
    base: 0,
    raised: 100,
    navigation: 200,
    overlay: 300,
    modal: 400,
    toast: 500,
    critical: 600,
};
/**
 * Z-index offset added per stacked modal on top of the `modal` tier.
 *
 * When multiple modals are open simultaneously (e.g. a confirmation dialog on
 * top of an edit dialog), each subsequent modal in the stack gets
 * `ELEVATION_Z_INDEX.modal + (stackIndex * MODAL_STACK_Z_OFFSET)`.
 */
export const MODAL_STACK_Z_OFFSET = 1;
/**
 * Computes the z-index for a modal at a given position in the stack.
 */
export function getModalZIndex(stackIndex) {
    return ELEVATION_Z_INDEX.modal + stackIndex * MODAL_STACK_Z_OFFSET;
}
/**
 * Computes the z-index for overlay components (Popover, Menu, Tooltip) that
 * portal into a Surface.
 *
 * When an overlay portals into a Surface that has an elevation tier, the
 * overlay's z-index is the sum of the parent Surface's z-index and the
 * `overlay` tier. This ensures the overlay renders above sibling Surfaces
 * that may paint later in the DOM (e.g. a side panel next to the Surface
 * the overlay portals into).
 *
 * When the parent Surface has no elevation (or is `base`), the overlay
 * still gets the base `overlay` tier z-index (300) so it rises above
 * adjacent elements in the same stacking context.
 */
export function getOverlayZIndex(parentElevation) {
    const parentZ = parentElevation ? ELEVATION_Z_INDEX[parentElevation] : 0;
    return parentZ + ELEVATION_Z_INDEX.overlay;
}
//# sourceMappingURL=elevation.js.map